<template>
  <!-- Invite people (the Invite and MacInvite boards; invite_sheet.dart),
       opened by the session page's Invite / Share buttons through open().
       Its Session.vue contract is frozen: the sessionName prop and open().
       From 700 px: a 330 px popover under the button that opened it, with
       an arrow (MacInvite): who can join, the link with Copy, people to
       pick, a note, who is in the session, then Cancel and Send to N.
       Under 700 px: the Invite sheet: Cancel, the title, the search and
       picked chips, the people groups, then a pinned footer with who can
       join and Send to N people. Sending is POST /invitations per person.
       Someone who isn't a member gets the link alone (inviting 403s).
       Closing hands focus back to the button that opened it. It emits
       open-change (true / false) so that button can draw pressed. -->
  <div class="invite-host">
    <!-- The popover (MacInvite) -->
    <v-overlay
      v-if="!phone"
      v-model="dialog"
      :scrim="false"
      location-strategy="static"
      scroll-strategy="none"
      content-class="invite-pop-content"
      :content-props="{ style: popStyle }"
    >
      <div class="invite-pop" role="dialog" :aria-label="title" data-test="invite-popover">
        <svg class="invite-arrow" :style="{ left: arrowLeft + 'px' }" width="20" height="10" viewBox="0 0 20 10" aria-hidden="true">
          <path d="M0 10 L10 0 L20 10 Z" class="invite-arrow-fill" />
          <path d="M0.5 10 L10 0.7 L19.5 10" class="invite-arrow-edge" fill="none" />
        </svg>
        <div class="ip-title">{{ title }}</div>

        <template v-if="knowVisibility">
          <div class="ip-access" :class="{ 'is-readonly': !isOwner }">
            <ClSegmented :model-value="privacy" :options="ACCESS" size="regular" label="Who can join" data-test="invite-access" @update:model-value="setPrivacy" />
          </div>
          <div v-if="!isOwner && resolvedSession" class="ip-note">Only {{ resolvedSession.creator }} can change who can join.</div>
        </template>

        <div class="ip-link">
          <span class="ip-link-text" data-test="invite-link">{{ shownLink }}</span>
          <button type="button" class="ip-copy" data-test="invite-copy" @click="copyLink">{{ copied ? "Copied" : "Copy" }}</button>
        </div>

        <template v-if="canInvite">
          <ToolbarSearch v-model="query" class="ip-search" variant="list" placeholder="Name or email" aria-label="Find people" />
          <div class="ip-picks" :class="{ 'is-scroll': picks.length > 4 }">
            <button
              v-for="p in picks"
              :key="p.id"
              type="button"
              role="checkbox"
              class="ip-pick"
              :aria-checked="isPicked(p.id) ? 'true' : 'false'"
              :data-test="`invite-person-${p.id}`"
              @click="toggle(p.id)"
            >
              <span class="ip-check" :class="{ 'is-on': isPicked(p.id) }">
                <svg v-if="isPicked(p.id)" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7" /></svg>
              </span>
              <span v-if="p.email" class="ip-mail" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
              </span>
              <PersonDot v-else :name="p.name" :color="colorOf(p.id)" :picture="p.picture" :size="24" />
              <span class="ip-name">{{ p.name }}</span>
              <span v-if="p.sub" class="ip-sub">{{ p.sub }}</span>
            </button>
            <p v-if="!picks.length" class="ip-empty">{{ query.trim() ? "No one by that name yet. Try an email." : "Search by name, or type an email." }}</p>
          </div>
          <textarea v-model="message" class="ip-message" maxlength="200" placeholder="Add a note (optional)" aria-label="Note" data-test="invite-note" />
          <div class="ip-section">In this session</div>
          <div v-for="m in members" :key="m.username" class="ip-member" :data-test="`invite-member-${m.username}`">
            <PersonDot :name="m.name" :color="m.color" :picture="m.picture" :size="22" />
            <span class="ip-member-name">{{ m.label }}</span>
            <span v-if="m.host" class="ip-role">Host</span>
            <button v-else-if="canRemove(m.username)" type="button" class="ip-remove" :disabled="removing === m.username" @click="remove(m)">Remove</button>
          </div>
        </template>
        <p v-else class="ip-note ip-hint">{{ copyHint }}</p>

        <div class="ip-actions">
          <button v-if="canNativeShare && !canInvite" type="button" class="ip-btn" @click="shareNative">Share…</button>
          <button type="button" class="ip-btn" data-test="invite-cancel" @click="dialog = false">{{ canInvite ? "Cancel" : "Done" }}</button>
          <button v-if="canInvite" type="button" class="ip-btn ip-btn--send" :disabled="!picked.length || sending" data-test="invite-send" @click="send">
            {{ picked.length ? `Send to ${picked.length}` : "Send" }}
          </button>
        </div>
      </div>
    </v-overlay>

    <!-- The sheet (Invite) -->
    <SocialSheet v-else v-model="dialog" :label="title">
      <template #header>
        <div class="is-head">
          <button type="button" class="is-cancel" data-test="invite-cancel" @click="dialog = false">{{ canInvite ? "Cancel" : "Done" }}</button>
          <div class="is-title">{{ title }}</div>
          <span class="is-head-spacer" aria-hidden="true" />
        </div>
      </template>

      <div class="is-body" data-test="invite-sheet">
        <template v-if="canInvite">
          <ToolbarSearch v-model="query" class="is-search" variant="phone" placeholder="Name or email" aria-label="Search people" />
          <div v-if="picked.length" class="is-chips">
            <button v-for="id in picked" :key="id" type="button" class="is-chip" :aria-label="`Remove ${nameOf(id)}`" :data-test="`invite-chip-${id}`" @click="toggle(id)">
              <span v-if="isEmail(id)" class="is-chip-mail" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
              </span>
              <PersonDot v-else :name="nameOf(id)" :color="colorOf(id)" :picture="pictureOf(id)" :size="24" />
              <span>{{ nameOf(id) }}</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" aria-hidden="true"><path d="M7 7l10 10M17 7 7 17" /></svg>
            </button>
          </div>

          <section v-for="g in groups" :key="g.key" class="is-group" :data-test="`invite-${g.key}`">
            <div v-if="g.title" class="is-label">{{ g.title }}</div>
            <div v-else class="is-gap" />
            <div class="is-card">
              <button
                v-for="p in g.rows"
                :key="p.id"
                type="button"
                role="checkbox"
                class="is-row"
                :class="{ 'is-here': p.here }"
                :disabled="p.here"
                :aria-checked="isPicked(p.id) ? 'true' : 'false'"
                :data-test="`invite-person-${p.id}`"
                @click="toggle(p.id)"
              >
                <span v-if="p.email" class="is-mail" aria-hidden="true">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
                </span>
                <PersonDot v-else :name="p.name" :color="colorOf(p.id)" :picture="p.picture" :size="36" />
                <span class="is-row-main">
                  <span class="is-row-text">
                    <span class="is-row-name">{{ p.name }}</span>
                    <span v-if="p.sub" class="is-row-sub">{{ p.sub }}</span>
                  </span>
                  <span class="is-circle" :class="{ 'is-on': isPicked(p.id) }">
                    <svg v-if="isPicked(p.id)" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7" /></svg>
                  </span>
                </span>
              </button>
            </div>
          </section>
          <p v-if="!groups.length" class="is-empty">{{ query.trim() ? "No one by that name yet. Try an email." : "Search by name, or type an email." }}</p>

          <section v-if="members.length" class="is-group" data-test="invite-members">
            <div class="is-label">In this session</div>
            <div class="is-card">
              <div v-for="m in members" :key="m.username" class="is-row is-member">
                <PersonDot :name="m.name" :color="m.color" :picture="m.picture" :size="36" />
                <span class="is-row-main">
                  <span class="is-row-text"><span class="is-row-name">{{ m.label }}</span></span>
                  <span v-if="m.host" class="is-role">Host</span>
                  <button v-else-if="canRemove(m.username)" type="button" class="is-remove" :disabled="removing === m.username" @click="remove(m)">Remove</button>
                </span>
              </div>
            </div>
          </section>

          <section class="is-group">
            <div class="is-label">Note</div>
            <textarea v-model="message" class="is-message" maxlength="200" placeholder="Add a note (optional)" aria-label="Note" />
          </section>
        </template>

        <section class="is-group" data-test="invite-link-group">
          <div class="is-label">Session link</div>
          <div class="is-link">
            <span class="is-link-text" data-test="invite-link">{{ shownLink }}</span>
            <button type="button" class="is-copy" data-test="invite-copy" @click="copyLink">{{ copied ? "Copied" : "Copy" }}</button>
            <button v-if="canNativeShare" type="button" class="is-share" aria-label="Share link" @click="shareNative">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3v12M8 7l4-4 4 4" /><path d="M6 11v8a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-8" /></svg>
            </button>
          </div>
          <p class="is-hint">{{ copyHint }}</p>
        </section>
      </div>

      <template v-if="canInvite" #footer>
        <div class="is-foot">
          <div class="is-foot-note" data-test="invite-footer-note">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
            {{ footerNote }}
          </div>
          <button type="button" class="is-send" :disabled="!picked.length || sending" data-test="invite-send" @click="send">
            {{ picked.length ? `Send to ${picked.length} ${picked.length === 1 ? "person" : "people"}` : "Send" }}
          </button>
        </div>
      </template>
    </SocialSheet>
  </div>
</template>

<script>
import cloudloop from "@/mixins/cloudloop";
import ClSegmented from "@/components/kit/ClSegmented.vue";
import PersonDot from "@/components/kit/PersonDot.vue";
import ToolbarSearch from "@/components/kit/ToolbarSearch.vue";
import SocialSheet from "@/components/social/SocialSheet.vue";
import * as social from "@/services/api/social.js";
import { apiGet } from "@/services/api/client.js";
import { sessionTitle } from "@/kit/sessionName.js";
import { firstName, isEmail, personFromRow, shownName, when } from "@/kit/presence.js";
import { assign, forName, joinOrder } from "@/kit/people.js";
import { invitable } from "@/kit/testAccounts.js";
import { viewport, PHONE_MAX } from "@/shell/sizeClass.js";
import { confirm } from "@/shell/confirm.js";
import { toast } from "@/shell/toast.js";

const ACCESS = [
  { value: "invite", label: "Invite only" },
  { value: "link", label: "Link" },
  { value: "public", label: "Public" },
];
// How many people "Played with recently" lists (Invite board).
const RECENT_SHOWN = 3;
const POP_W = 330;

export default {
  name: "InviteDialog",
  components: { ClSegmented, PersonDot, ToolbarSearch, SocialSheet },
  mixins: [cloudloop],
  props: {
    sessionName: {
      type: String,
      required: true,
    },
  },
  emits: ["open-change"],
  data() {
    return {
      ACCESS,
      dialog: false,
      query: "",
      message: "",
      picked: [],
      following: [],
      details: {},
      recent: [],
      found: [],
      searched: "",
      sending: false,
      copied: false,
      visibilityBusy: false,
      removing: null,
      removeUnavailable: false,
      anchorRect: null,
      fetched: null,
    };
  },
  computed: {
    me() {
      return this.$store.state.loggedInUser?.username || null;
    },
    phone() {
      return viewport.width < PHONE_MAX;
    },
    // The store's selected session, trusted only when it is this one;
    // else the doc fetched on open (a row's menu opened it).
    resolvedSession() {
      const s = this.$store.state.selectedSession;
      if (s && s.name === this.sessionName) return s;
      const f = this.fetched;
      return f && f.name === this.sessionName ? f : null;
    },
    displayName() {
      return sessionTitle({ name: this.sessionName, alt_name: this.resolvedSession?.alt_name });
    },
    knowVisibility() {
      return !!this.resolvedSession;
    },
    // The server's privacy_of: `private` wins, and a private session
    // without a valid `privacy` is a link session.
    privacy() {
      const s = this.resolvedSession;
      if (!s) return null;
      const p = s.private;
      const isPrivate = typeof p === "string" ? p.trim().toLowerCase() === "true" : !!p;
      if (!isPrivate) return "public";
      return s.privacy === "invite" ? "invite" : "link";
    },
    isOwner() {
      return !!this.me && !!this.resolvedSession && this.resolvedSession.creator === this.me;
    },
    // Inviting is members-only (the endpoint 403s otherwise).
    canInvite() {
      if (!this.me) return false;
      const users = this.resolvedSession && this.resolvedSession.users;
      if (!Array.isArray(users)) return this.isOwner;
      return users.includes(this.me) || this.isOwner;
    },
    title() {
      return this.canInvite ? `Invite to ${this.displayName}` : `Share ${this.displayName}`;
    },
    // /s/<name>?via=<me>: the share route, which unfurls a card and
    // attributes sign-ups to the sharer.
    shareUrl() {
      const via = this.me ? `?via=${encodeURIComponent(this.me)}` : "";
      return `${window.location.origin}/s/${encodeURIComponent(this.sessionName)}${via}`;
    },
    // Shown without the attribution (Copy keeps it).
    shownLink() {
      return `${window.location.host}/s/${this.sessionName}`;
    },
    canNativeShare() {
      return typeof navigator !== "undefined" && !!navigator.share;
    },
    copyHint() {
      return this.privacy === "invite"
        ? "This link only opens for members and invited people."
        : "Anyone with the link can listen, no account needed.";
    },
    footerNote() {
      if (this.privacy === "link") return "Anyone with the link can join. They'll get it in their inbox.";
      if (this.privacy === "public") return "Public. They'll get it in their inbox.";
      return "Invite only. They'll get it in their inbox.";
    },
    memberNames() {
      const s = this.resolvedSession;
      if (!s) return [];
      return joinOrder({ creator: s.creator, users: s.users || [] });
    },
    memberColors() {
      return assign(this.memberNames, this.me);
    },
    members() {
      const s = this.resolvedSession;
      const names = (s && s.member_display_names) || {};
      return this.memberNames.map((u) => {
        const shown = shownName(u, names[u] || (this.details[u] && this.details[u].displayName));
        return {
          username: u,
          name: shown,
          label: u === this.me ? `${u} (you)` : shown,
          color: this.memberColors[u] || forName(u, this.me),
          picture: this.pictureOf(u),
          host: !!s && u === s.creator,
        };
      });
    },
    typedEmail() {
      const q = this.query.trim();
      return isEmail(q) ? q : null;
    },
    recentShown() {
      return this.recent.slice(0, RECENT_SHOWN).filter((p) => this.matches(p.username));
    },
    recentNames() {
      return this.recentShown.map((p) => p.username);
    },
    matchingFollowing() {
      return this.following.filter((u) => this.matches(u) && !this.recentNames.includes(u));
    },
    others() {
      if (this.query.trim() !== this.searched) return [];
      return this.found.filter((u) => !this.following.includes(u) && !this.recentNames.includes(u));
    },
    // The sheet's groups (Invite board).
    groups() {
      const out = [];
      if (this.typedEmail) out.push({ key: "email", title: "", rows: [this.emailRow(this.typedEmail)] });
      if (this.recentShown.length) {
        out.push({ key: "recent", title: "Played with recently", rows: this.recentShown.map((p) => this.row(p.username, this.recentSub(p))) });
      }
      if (this.matchingFollowing.length) {
        out.push({ key: "friends", title: "Friends", rows: this.matchingFollowing.slice(0, 30).map((u) => this.row(u, this.friendSub(u, false))) });
      }
      if (this.others.length) {
        out.push({ key: "more", title: "More people", rows: this.others.slice(0, 20).map((u) => this.row(u, "")) });
      }
      return out;
    },
    // The popover's one list: members are under In this session instead.
    picks() {
      const out = [];
      if (this.typedEmail) out.push(this.emailRow(this.typedEmail));
      for (const p of this.recentShown) if (!this.isMember(p.username)) out.push(this.row(p.username, this.recentSub(p)));
      for (const u of this.matchingFollowing.slice(0, 30)) if (!this.isMember(u)) out.push(this.row(u, this.friendSub(u, true)));
      for (const u of this.others.slice(0, 20)) if (!this.isMember(u)) out.push(this.row(u, ""));
      return out;
    },
    popLeft() {
      const r = this.anchorRect;
      const vw = viewport.width || 1280;
      if (!r) return Math.max(10, (vw - POP_W) / 2);
      const centre = r.left + r.width / 2;
      return Math.min(vw - POP_W - 10, Math.max(10, centre - 215));
    },
    arrowLeft() {
      const r = this.anchorRect;
      if (!r) return POP_W / 2 - 10;
      return Math.min(POP_W - 30, Math.max(10, r.left + r.width / 2 - this.popLeft - 10));
    },
    popStyle() {
      const r = this.anchorRect;
      return { top: `${r ? r.bottom + 12 : 64}px`, left: `${this.popLeft}px`, width: `${POP_W}px` };
    },
  },
  watch: {
    // Closed: focus goes back to whatever opened it.
    dialog(open) {
      this.$emit("open-change", !!open);
      if (open) return;
      const el = this.returnFocus;
      this.returnFocus = null;
      if (!el) return;
      setTimeout(() => {
        if (el.isConnected && el.offsetParent !== null) el.focus({ preventScroll: true });
      }, 0);
    },
    query(q) {
      clearTimeout(this.searchTimer);
      const text = (q || "").trim();
      if (text.length < 2 || isEmail(text)) {
        this.found = [];
        this.searched = text;
        return;
      }
      const seq = (this.searchSeq = (this.searchSeq || 0) + 1);
      this.searchTimer = setTimeout(async () => {
        try {
          const rows = await social.searchUsers(text);
          if (seq !== this.searchSeq) return;
          // Test accounts never, even ones you follow (plan W4's Invite).
          this.found = this.absorb(rows).filter((u) => invitable(u, this.me));
          this.searched = text;
        } catch (e) {
          /* keep what it shows */
        }
      }, 250);
    },
  },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
    if (this.onResize) window.removeEventListener("resize", this.onResize);
  },
  methods: {
    isEmail,
    // The page's Invite / Share buttons open it. `returnTo`: the element
    // to focus when it closes, and the popover's anchor.
    open(returnTo) {
      this.returnFocus =
        returnTo instanceof HTMLElement
          ? returnTo
          : document.activeElement instanceof HTMLElement && document.activeElement !== document.body
            ? document.activeElement
            : null;
      this.measure();
      if (!this.onResize) {
        this.onResize = () => this.measure();
        window.addEventListener("resize", this.onResize);
      }
      this.picked = [];
      this.query = "";
      this.message = "";
      this.dialog = true;
      if (this.resolvedSession) {
        if (this.canInvite) this.loadPeople();
      } else {
        this.fetchSession();
      }
    },
    async fetchSession() {
      try {
        const doc = await apiGet("/session", { session_name: this.sessionName });
        if (doc && doc.name === this.sessionName) this.fetched = doc;
      } catch (e) {
        /* the link alone */
      }
      if (this.canInvite) this.loadPeople();
    },
    measure() {
      const el = this.returnFocus;
      if (el && el.isConnected) {
        const r = el.getBoundingClientRect();
        this.anchorRect = { left: r.left, top: r.top, width: r.width, bottom: r.bottom };
      } else {
        this.anchorRect = null;
      }
    },
    absorb(rows) {
      const names = [];
      const next = { ...this.details };
      for (const row of rows || []) {
        const p = personFromRow(row);
        if (!p) continue;
        names.push(p.username);
        if (p.detailed) next[p.username] = { ...(next[p.username] || {}), ...p };
      }
      this.details = next;
      return names;
    },
    async loadPeople() {
      const [f, r] = await Promise.allSettled([social.getFollowing(), social.getRecentPeople(12)]);
      if (f.status === "fulfilled") {
        const names = this.absorb(f.value).filter((u) => invitable(u, this.me));
        this.following = [...new Set(names)].sort((a, b) => this.nameOf(a).toLowerCase().localeCompare(this.nameOf(b).toLowerCase()));
      }
      if (r.status === "fulfilled") {
        const rows = r.value.filter((row) => row && invitable(row.username, this.me));
        this.absorb(rows);
        this.recent = rows.map((row) => personFromRow(row)).filter(Boolean);
      }
    },
    matches(u) {
      const q = this.query.trim().toLowerCase();
      if (!q) return true;
      return u.toLowerCase().includes(q) || this.nameOf(u).toLowerCase().includes(q);
    },
    isMember(u) {
      return this.memberNames.includes(u);
    },
    nameOf(id) {
      if (isEmail(id)) return id;
      const d = this.details[id];
      return shownName(id, d && d.displayName);
    },
    pictureOf(id) {
      const d = this.details[id];
      return (d && d.picture) || null;
    },
    colorOf(id) {
      return forName(id, this.me);
    },
    row(u, sub) {
      const here = this.isMember(u);
      return { id: u, name: this.nameOf(u), picture: this.pictureOf(u), here, sub: here ? "Already in this session" : sub };
    },
    emailRow(email) {
      return { id: email, name: email, email: true, sub: "Invite by email" };
    },
    // "In Friday jam · Tue", or "3 sessions together".
    recentSub(p) {
      if (p.lastSessionName && p.lastPlayedAt) {
        return `In ${sessionTitle({ name: p.lastSessionName })} · ${when(p.lastPlayedAt, new Date())}`;
      }
      const n = p.sharedSessionCount || 0;
      if (n <= 0) return "";
      return n === 1 ? "1 session together" : `${n} sessions together`;
    },
    // "Playing now", else when you last played together.
    friendSub(u, compact) {
      const d = this.details[u];
      if (d && d.live) return "Playing now";
      const r = this.recent.find((p) => p.username === u);
      if (!r || !r.lastPlayedAt) return "";
      const w = when(r.lastPlayedAt, new Date());
      return compact ? `Played ${w}` : `Played together ${w}`;
    },
    isPicked(id) {
      return this.picked.includes(id);
    },
    toggle(id) {
      if (this.isMember(id)) return;
      this.picked = this.isPicked(id) ? this.picked.filter((x) => x !== id) : [...this.picked, id];
    },
    async setPrivacy(value) {
      if (!this.isOwner || this.visibilityBusy || this.privacy === value) return;
      this.visibilityBusy = true;
      try {
        const updated = await this.setSessionPrivacy(this.sessionName, value);
        if (updated) this.keepSession(updated);
      } catch (e) {
        toast(
          (e?.status === 409 && e.message) ||
            (e?.status === 403 ? "Only the session owner can change who can join." : "Couldn't change who can join. Try again.")
        );
      } finally {
        this.visibilityBusy = false;
      }
    },
    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.shareUrl);
        this.copied = true;
        setTimeout(() => (this.copied = false), 1500);
        toast("Session link copied");
      } catch (e) {
        toast(`Copy this link: ${this.shareUrl}`, { timeout: 8000 });
      }
    },
    shareNative() {
      navigator
        .share({
          title: `${this.displayName} · cloudloop`,
          text: `Jam on "${this.displayName}" with me on cloudloop`,
          url: this.shareUrl,
        })
        .catch(() => {});
    },
    // A changed doc goes where this one came from: the open session's
    // store copy, or the one fetched for a row.
    keepSession(doc) {
      const s = this.$store.state.selectedSession;
      if (s && s.name === this.sessionName) this.$store.commit("setSelectedSession", doc);
      else this.fetched = doc;
    },
    canRemove(u) {
      const s = this.resolvedSession;
      return this.isOwner && !this.removeUnavailable && !!s && u !== s.creator && u !== this.me;
    },
    async remove(m) {
      const ok = await confirm({
        title: `Remove ${m.name}?`,
        body: `They'll leave ${this.displayName} now. Their layers stay in the session.`,
        confirmLabel: "Remove",
        destructive: true,
      });
      if (!ok) return;
      this.removing = m.username;
      try {
        const doc = await social.removeMember(this.sessionName, m.username);
        if (doc && doc.name === this.sessionName) this.keepSession(doc);
        toast(`Removed ${m.name}`);
      } catch (e) {
        const notMember = e && e.body && /not_member/.test(JSON.stringify(e.body));
        if (e && (e.status === 405 || (e.status === 404 && !notMember))) {
          this.removeUnavailable = true;
        } else if (notMember) {
          toast(`${m.name} already left`);
        } else {
          toast(`Couldn't remove ${m.name}. Try again.`);
        }
      } finally {
        this.removing = null;
      }
    },
    async send() {
      if (!this.picked.length || this.sending) return;
      this.sending = true;
      const note = this.message.trim() || null;
      const ids = [...this.picked];
      const failed = [];
      let lastError = null;
      for (const id of ids) {
        try {
          await social.sendInvitation({
            sessionName: this.sessionName,
            username: isEmail(id) ? null : id,
            email: isEmail(id) ? id : null,
            message: note,
          });
        } catch (e) {
          failed.push(id);
          lastError = e;
        }
      }
      this.sending = false;
      if (!failed.length) {
        toast(ids.length === 1 ? "Invite sent" : `Invites sent to ${ids.length} people`);
        this.dialog = false;
        return;
      }
      this.picked = failed;
      const status = lastError && lastError.status;
      const who = failed.length === 1 ? firstName(this.nameOf(failed[0])) : `${failed.length} people`;
      if (status === 409) toast(`${who} already has an invite to this session.`);
      else if (status === 403) toast("Only people in this session can invite others.");
      else toast(`Couldn't invite ${who}. Try again.`);
    },
  },
};
</script>

<style scoped>
.invite-host {
  display: contents;
}

/* ---- The popover (MacInvite) ---- */
.invite-pop {
  position: relative;
  width: 330px;
  border-radius: 12px;
  background: var(--cl-popover);
  border: 1px solid var(--cl-border-default);
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.6);
  padding: 14px;
  color: var(--cl-label);
  font-family: var(--cl-font);
}
.invite-arrow {
  position: absolute;
  top: -10px;
}
.invite-arrow-fill {
  fill: var(--cl-popover);
}
.invite-arrow-edge {
  stroke: var(--cl-border-default);
}
.ip-title {
  font: 700 15px/20px var(--cl-font);
}
.ip-access {
  margin-top: 10px;
}
.ip-access.is-readonly {
  pointer-events: none;
}
.ip-access :deep(.cl-seg) {
  width: 100%;
}
.ip-note {
  margin: 6px 4px 0;
  font: 400 11px/14px var(--cl-font);
  color: var(--cl-label-3);
}
.ip-hint {
  margin-top: 10px;
}
.ip-link {
  margin-top: 10px;
  height: 30px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 4px 0 10px;
  border-radius: 7px;
  background: rgba(118, 118, 128, 0.18);
}
.ip-link-text {
  flex: 1;
  min-width: 0;
  font: 400 12px/16px var(--cl-font);
  color: rgba(235, 235, 245, 0.75);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ip-copy {
  height: 24px;
  padding: 0 10px;
  border: none;
  border-radius: 5px;
  background: rgba(118, 118, 128, 0.3);
  color: var(--cl-label);
  font: 600 12px/1 var(--cl-font);
  cursor: pointer;
}
.ip-search {
  margin-top: 12px;
  width: 100%;
  max-width: none;
}
.ip-picks {
  margin-top: 6px;
}
.ip-picks.is-scroll {
  max-height: 152px;
  overflow-y: auto;
}
.ip-pick {
  width: 100%;
  height: 38px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 4px;
  border: none;
  background: none;
  color: var(--cl-label);
  text-align: left;
  cursor: pointer;
  border-radius: 6px;
}
.ip-pick:hover {
  background: var(--cl-hover);
}
.ip-check {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  border-radius: 4px;
  border: 1.5px solid rgba(235, 235, 245, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--cl-on-fill);
}
.ip-check.is-on {
  border: none;
  background: var(--cl-accent);
}
.ip-mail {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--cl-label-2);
}
.ip-name {
  flex: 1;
  min-width: 0;
  font: 400 13px/16px var(--cl-font);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ip-sub {
  flex-shrink: 0;
  font: 400 11px/14px var(--cl-font);
  color: var(--cl-label-45);
}
.ip-empty {
  margin: 4px 4px 0;
  font: 400 11px/14px var(--cl-font);
  color: var(--cl-label-3);
}
.ip-message {
  display: block;
  margin-top: 8px;
  width: 100%;
  height: 50px;
  resize: none;
  border-radius: 7px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(118, 118, 128, 0.12);
  color: var(--cl-label);
  font: 400 12px/16px var(--cl-font);
  padding: 7px 9px;
  outline: none;
}
.ip-message::placeholder {
  color: var(--cl-label-4);
}
.ip-section {
  margin-top: 12px;
  font: 600 11px/14px var(--cl-font);
  color: var(--cl-label-45);
}
.ip-member {
  height: 32px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 4px;
}
.ip-member-name {
  flex: 1;
  min-width: 0;
  font: 400 13px/16px var(--cl-font);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ip-role {
  font: 400 12px/16px var(--cl-font);
  color: var(--cl-label-45);
}
.ip-remove {
  border: none;
  background: none;
  padding: 6px 0;
  font: 400 12px/16px var(--cl-font);
  color: var(--cl-destructive-text);
  cursor: pointer;
}
.ip-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}
.ip-btn {
  height: 28px;
  padding: 0 14px;
  border: none;
  border-radius: 7px;
  background: var(--cl-control-fill-strong);
  color: var(--cl-label);
  font: 400 13px/1 var(--cl-font);
  cursor: pointer;
  white-space: nowrap;
}
.ip-btn--send {
  background: var(--cl-accent);
  color: var(--cl-on-fill);
  font-weight: 600;
}
.ip-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

/* ---- The sheet (Invite) ---- */
.is-head {
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 8px;
}
.is-cancel {
  height: 44px;
  min-width: 66px;
  padding: 0 8px;
  border: none;
  background: none;
  color: var(--cl-accent-text);
  font: 400 17px/22px var(--cl-font);
  text-align: left;
  cursor: pointer;
}
.is-title {
  flex: 1;
  min-width: 0;
  text-align: center;
  font: 600 17px/22px var(--cl-font);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.is-head-spacer {
  width: 66px;
  flex-shrink: 0;
}
.is-body {
  padding: 4px 16px 24px;
}
.is-search {
  width: 100%;
  max-width: none;
}
.is-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 12px;
}
.is-chip {
  height: 32px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 6px 0 4px;
  border: none;
  border-radius: 16px;
  background: var(--cl-accent-fill);
  color: var(--cl-accent-chip-text);
  font: 400 15px/1 var(--cl-font);
  cursor: pointer;
}
.is-chip-mail {
  width: 24px;
  display: flex;
  justify-content: center;
}
.is-label {
  padding: 18px 0 7px 16px;
  font: 400 13px/16px var(--cl-font);
  color: var(--cl-label-2);
}
.is-gap {
  height: 18px;
}
.is-card {
  border-radius: var(--cl-r-xl);
  background: var(--cl-cell-inner);
  overflow: hidden;
}
.is-row {
  width: 100%;
  height: 56px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 0 0 14px;
  border: none;
  background: none;
  color: var(--cl-label);
  text-align: left;
  font-family: var(--cl-font);
  cursor: pointer;
}
.is-row.is-here {
  opacity: 0.45;
  cursor: default;
}
.is-row-main {
  flex: 1;
  min-width: 0;
  height: 56px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding-right: 14px;
  border-bottom: 1px solid var(--cl-separator);
}
.is-row:last-child .is-row-main {
  border-bottom-color: transparent;
}
.is-row-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.is-row-name {
  font: 400 17px/22px var(--cl-font);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.is-row-sub {
  font: 400 13px/16px var(--cl-font);
  color: var(--cl-label-50);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.is-circle {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 12px;
  border: 2px solid rgba(235, 235, 245, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--cl-on-fill);
}
.is-circle.is-on {
  border: none;
  background: var(--cl-accent);
}
.is-mail {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--cl-accent-text);
}
.is-member {
  cursor: default;
}
.is-role {
  font: 400 15px/20px var(--cl-font);
  color: var(--cl-label-45);
}
.is-remove {
  border: none;
  background: none;
  padding: 8px 0;
  font: 400 15px/20px var(--cl-font);
  color: var(--cl-destructive-text);
  cursor: pointer;
}
.is-message {
  display: block;
  width: 100%;
  min-height: 72px;
  resize: vertical;
  border: none;
  border-radius: var(--cl-r-xl);
  background: var(--cl-cell-inner);
  color: var(--cl-label);
  font: 400 17px/22px var(--cl-font);
  padding: 10px 12px;
  outline: none;
}
.is-message::placeholder {
  color: var(--cl-label-4);
}
.is-link {
  height: 44px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 4px 0 12px;
  border-radius: var(--cl-r-xl);
  background: var(--cl-cell-inner);
}
.is-link-text {
  flex: 1;
  min-width: 0;
  font: 400 15px/20px var(--cl-font);
  color: var(--cl-label-2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.is-copy {
  height: 32px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background: var(--cl-control-fill);
  color: var(--cl-label);
  font: 600 15px/1 var(--cl-font);
  cursor: pointer;
}
.is-share {
  width: 36px;
  height: 36px;
  border: none;
  background: none;
  color: var(--cl-accent-text);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.is-hint,
.is-empty {
  margin: 8px 4px 0;
  font: 400 13px/18px var(--cl-font);
  color: var(--cl-label-3);
}
.is-foot {
  padding: 12px 16px 40px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: var(--cl-sheet);
}
.is-foot-note {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 4px 10px;
  font: 400 13px/16px var(--cl-font);
  color: var(--cl-label-55);
}
.is-send {
  width: 100%;
  height: 52px;
  border: none;
  border-radius: 14px;
  background: var(--cl-accent);
  color: var(--cl-on-fill);
  font: 600 17px/1 var(--cl-font);
  cursor: pointer;
}
.is-send:disabled {
  opacity: 0.45;
  cursor: default;
}
</style>

<style>
/* The popover's overlay content teleports to <body>. */
.v-overlay__content.invite-pop-content {
  position: fixed !important;
}
</style>
